import { Box, Grid } from '@mui/material';
import { Pagination } from '@mui/material';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  CardComponent,
  DashedStandardDropdown,
  BasicTable,
  Columns,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import React, { useEffect, useState } from 'react';
import {
  getDropdownValue,
  getTopDiagnosis,
  getTopMedications,
  getTopProcedures,
  getTopProviders,
} from '../../../core-utils/helper-utils/memberSummaryHelper';
import diseasePrevalencePageConstants from '../../../core-utils/messages/diseasePrevalencePageConstants';
import { useStores } from '../../../stores';
import { CircularLoader } from '@HomaHealth/ui-component-library';

export interface TopConditionsTableProps {
  startDate: string;
  endDate: string;
  memberList: string[];
  columns?: any;
  isDiseaseGroup?: boolean;
  condition?: string;
  isDiseasePrevalence?: boolean;
  isChronicPrevalence?: boolean;
}

const TopConditionsTable: React.FC<TopConditionsTableProps> = ({
  startDate,
  endDate,
  memberList,
  isDiseaseGroup,
  condition,
  isDiseasePrevalence,
  isChronicPrevalence,
}) => {
  const cardMessages = diseasePrevalencePageConstants.MEMBER_SUMMARY_CARDS;

  const [pageNumberForTopDiagnosis, setPageNumberForTopDiagnosis] = useState(1);
  const [
    pageNumberForTopMedications,
    setPageNumberForTopMedications,
  ] = useState(1);
  const [pageNumberForTopProviders, setPageNumberForTopProviders] = useState(1);
  const [pageNumberForTopProcedures, setPageNumberForTopProcedures] = useState(
    1,
  );

  const [
    totalPagesForTopMedications,
    setTotalPagesForTopMedications,
  ] = useState<number>(0);
  const [
    totalPagesForTopDiagnosis,
    setTotalPagesForTopDiagnosis,
  ] = useState<number>(0);
  const [
    totalPagesForTopProcedures,
    setTotalPagesForTopProcedures,
  ] = useState<number>(0);
  const [
    totalPagesForTopProviders,
    setTotalPagesForTopProviders,
  ] = useState<number>(0);

  const [diagnosisDropdownValue, setDiagnosisDropdownValue] = useState<string>(
    'Diagnosis by Cost',
  );
  const [
    medicationsDropdownValue,
    setMedicationsDropdownValue,
  ] = useState<string>('Medications by Cost');
  const [
    proceduresDropdownValue,
    setProceduresDropdownValue,
  ] = useState<string>('Procedures by Cost');
  const [providersDropdownValue, setProvidersDropdownValue] = useState<string>(
    'Providers by Cost',
  );

  const [diagnosisByCost, setDiagnosisByCost] = useState<any>([]);
  const [diagnosisByMember, setDiagnosisByMember] = useState<any>([]);
  const [proceduresByCost, setProceduresByCost] = useState<any>([]);
  const [proceduresByMember, setProceduresByMember] = useState<any>([]);
  const [providersByCost, setProvidersByCost] = useState<any>([]);
  const [providersByMember, setProvidersByMember] = useState<any>([]);
  const [medicationsByCost, setMedicationsByCost] = useState<any>([]);
  const [medicationsByMember, setMedicationsByMember] = useState<any>([]);

  const [diagnosisLoader, setDiagnosisLoader] = useState<boolean>(false);
  const [proceduresLoader, setProceduresLoader] = useState<boolean>(false);
  const [providersLoader, setProvidersLoader] = useState<boolean>(false);
  const [medicationsLoader, setMedicationsLoader] = useState<boolean>(false);

  const employerGroupStore = useStores().employerGroupStore;

  const requestBodyForMedications = {
    startDate: startDate,
    endDate: endDate,
    eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
    member_unique_id_list: memberList,
    pageNumber: pageNumberForTopMedications - 1,
    claim_id_list: [],
    isDiseaseGroup: isDiseaseGroup,
    condition: condition,
    max_claim_date:
      employerGroupStore?.employerGroupResponseData?.max_claim_date,
  };

  const requestBodyForDiagnosis = {
    startDate: startDate,
    endDate: endDate,
    eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
    member_unique_id_list: memberList,
    pageNumber: pageNumberForTopDiagnosis - 1,
    claim_id_list: [],
    isDiseaseGroup: isDiseaseGroup,
    condition: condition,
    max_claim_date:
      employerGroupStore?.employerGroupResponseData?.max_claim_date,
  };

  const requestBodyForProcedures = {
    startDate: startDate,
    endDate: endDate,
    eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
    member_unique_id_list: memberList,
    pageNumber: pageNumberForTopProcedures - 1,
    isDiseaseGroup: isDiseaseGroup,
    condition: condition,
    max_claim_date:
      employerGroupStore?.employerGroupResponseData?.max_claim_date,
  };

  const requestBodyForProviders = {
    startDate: startDate,
    endDate: endDate,
    eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
    member_unique_id_list: memberList,
    pageNumber: pageNumberForTopProviders - 1,
    isDiseaseGroup,
    condition,
    max_claim_date:
      employerGroupStore?.employerGroupResponseData?.max_claim_date,
  };

  const cellStyles = {
    color: patientAccessTheme.palette.text.mediumemphasis,
    fontSize: '12px',
    fontWeight: 500,
  };

  const columnsForDiagnosis: Columns[] = [
    {
      name: 'Diagnosis',
      align: 'left',
      cellStyles: cellStyles,
      width: '423px',
    },
    {
      name: 'Percentage',
      align: 'center',
      cellStyles: cellStyles,
      width: '100px',
    },
  ];

  const columnsForMedications: Columns[] = [
    {
      name: 'Medication',
      align: 'left',
      cellStyles: cellStyles,
      width: '423px',
    },
    {
      name: 'Percentage',
      align: 'center',
      cellStyles: cellStyles,
      width: '100px',
    },
  ];

  const columnsForProcedures: Columns[] = [
    {
      name: 'Procedure',
      align: 'left',
      cellStyles: cellStyles,
      width: '423px',
    },
    {
      name: 'Percentage',
      align: 'center',
      cellStyles: cellStyles,
      width: '100px',
    },
  ];

  const columnsForProviders: Columns[] = [
    {
      name: 'Provider',
      align: 'left',
      cellStyles: cellStyles,
      width: '423px',
    },
    {
      name: 'Percentage',
      align: 'center',
      cellStyles: cellStyles,
      width: '100px',
    },
  ];

  const getDropdownDetails = (value: string) => {
    switch (value) {
      case 'Diagnosis by Cost':
        return diagnosisByCost;
      case 'Diagnosis by Member':
        return diagnosisByMember;
      case 'Medications by Cost':
        return medicationsByCost;
      case 'Medications by Member':
        return medicationsByMember;
      case 'Procedures by Cost':
        return proceduresByCost;
      case 'Procedures by Member':
        return proceduresByMember;
      case 'Providers by Cost':
        return providersByCost;
      case 'Providers by Member':
        return providersByMember;
      default:
        return null;
    }
  };

  const handlePageChangeForTopDiagnosis = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setPageNumberForTopDiagnosis(value);
  };

  const handlePageChangeForTopMedications = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setPageNumberForTopMedications(value);
  };

  const handlePageChangeForTopProcedures = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setPageNumberForTopProcedures(value);
  };
  const handlePageChangeForTopProviders = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setPageNumberForTopProviders(value);
  };

  useEffect(() => {
    if (
      medicationsDropdownValue.includes('Cost') &&
      Boolean(startDate) &&
      Boolean(endDate)
    ) {
      getTopMedications(
        requestBodyForMedications,
        'cost',
        setMedicationsByCost,
        setMedicationsLoader,
        setTotalPagesForTopMedications,
        isDiseasePrevalence,
        isChronicPrevalence,
      );
    } else {
      getTopMedications(
        requestBodyForMedications,
        'member',
        setMedicationsByMember,
        setMedicationsLoader,
        setTotalPagesForTopMedications,
        isDiseasePrevalence,
        isChronicPrevalence,
      );
    }
  }, [pageNumberForTopMedications, medicationsDropdownValue]);
  useEffect(() => {
    if (
      diagnosisDropdownValue.includes('Cost') &&
      Boolean(startDate) &&
      Boolean(endDate)
    ) {
      getTopDiagnosis(
        requestBodyForDiagnosis,
        'cost',
        setDiagnosisByCost,
        setDiagnosisLoader,
        setTotalPagesForTopDiagnosis,
        isDiseasePrevalence,
        isChronicPrevalence,
      );
    } else {
      getTopDiagnosis(
        requestBodyForDiagnosis,
        'member',
        setDiagnosisByMember,
        setDiagnosisLoader,
        setTotalPagesForTopDiagnosis,
        isDiseasePrevalence,
        isChronicPrevalence,
      );
    }
  }, [pageNumberForTopDiagnosis, diagnosisDropdownValue]);
  useEffect(() => {
    if (
      proceduresDropdownValue.includes('Cost') &&
      Boolean(startDate) &&
      Boolean(endDate)
    ) {
      getTopProcedures(
        requestBodyForProcedures,
        'cost',
        setProceduresByCost,
        setProceduresLoader,
        setTotalPagesForTopProcedures,
        isDiseasePrevalence,
        isChronicPrevalence,
      );
    } else {
      getTopProcedures(
        requestBodyForProcedures,
        'member',
        setProceduresByMember,
        setProceduresLoader,
        setTotalPagesForTopProcedures,
        isDiseasePrevalence,
        isChronicPrevalence,
      );
    }
  }, [pageNumberForTopProcedures, proceduresDropdownValue]);
  useEffect(() => {
    if (
      providersDropdownValue.includes('Cost') &&
      Boolean(startDate) &&
      Boolean(endDate)
    ) {
      getTopProviders(
        requestBodyForProviders,
        'cost',
        setProvidersByCost,
        setProvidersLoader,
        setTotalPagesForTopProviders,
        isDiseasePrevalence,
        isChronicPrevalence,
      );
    } else {
      getTopProviders(
        requestBodyForProviders,
        'member',
        setProvidersByMember,
        setProvidersLoader,
        setTotalPagesForTopProviders,
        isDiseasePrevalence,
        isChronicPrevalence,
      );
    }
  }, [pageNumberForTopProviders, providersDropdownValue]);

  return (
    <>
      <Grid style={{ display: 'flex', marginBottom: '28px' }}>
        <div style={{ marginRight: '24px' }}>
          <CardComponent
            title={cardMessages.TOP_DIAGNOSIS.TITLE}
            subTitle={
              cardMessages.TOP_DIAGNOSIS.SUB_TITLE +
              getDropdownValue(diagnosisDropdownValue)
            }
            dropdownElement={
              <DashedStandardDropdown
                options={cardMessages.TOP_DIAGNOSIS.DROPDOWN_OPTIONS}
                displayValue={cardMessages.TOP_DIAGNOSIS.DROPDOWN_OPTIONS}
                value={diagnosisDropdownValue}
                handleChange={(event: any) => {
                  const value: string = event.target.value as string;
                  setDiagnosisDropdownValue(value);
                  setPageNumberForTopDiagnosis(1);
                }}
              />
            }
            width="576px"
            height="405px"
            body={
              diagnosisLoader ? (
                <Box
                  style={{
                    display: 'flex',
                    height: '259px',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <CircularLoader />
                </Box>
              ) : (
                <>
                  <Grid style={{ margin: '7px 0px 0px 6px' }}>
                    <BasicTable
                      columns={columnsForDiagnosis}
                      rowsData={getDropdownDetails(diagnosisDropdownValue)}
                      displayType="row"
                      width="523px"
                      cellHeight="43px"
                      cellPadding="12px 18px 12px 28px"
                      tableHeight="259px"
                    />
                  </Grid>
                  <Grid
                    style={{
                      marginTop: '16px',
                      position: 'absolute',
                      right: '0px',
                      marginRight: '20px',
                    }}
                  >
                    <Pagination
                      page={pageNumberForTopDiagnosis}
                      count={totalPagesForTopDiagnosis}
                      onChange={handlePageChangeForTopDiagnosis}
                      siblingCount={1}
                      boundaryCount={1}
                      shape={'rounded'}
                      size="small"
                    />
                  </Grid>
                </>
              )
            }
          />
        </div>
        <CardComponent
          title={cardMessages.TOP_MEDICATIONS.TITLE}
          subTitle={
            cardMessages.TOP_MEDICATIONS.SUB_TITLE +
            getDropdownValue(medicationsDropdownValue)
          }
          dropdownElement={
            <DashedStandardDropdown
              options={cardMessages.TOP_MEDICATIONS.DROPDOWN_OPTIONS}
              displayValue={cardMessages.TOP_MEDICATIONS.DROPDOWN_OPTIONS}
              value={medicationsDropdownValue}
              handleChange={(event: any) => {
                const value: string = event.target.value as string;
                setMedicationsDropdownValue(value);
                setPageNumberForTopMedications(1);
              }}
            />
          }
          width="576px"
          height="405px"
          body={
            medicationsLoader ? (
              <Box
                style={{
                  display: 'flex',
                  height: '259px',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <CircularLoader />
              </Box>
            ) : (
              <>
                <Grid style={{ margin: '7px 0px 0px 6px' }}>
                  <BasicTable
                    columns={columnsForMedications}
                    rowsData={getDropdownDetails(medicationsDropdownValue)}
                    displayType="row"
                    width="523px"
                    cellHeight="43px"
                    cellPadding="12px 18px 12px 28px"
                    tableHeight="259px"
                  />
                </Grid>
                <Grid
                  style={{
                    marginTop: '16px',
                    position: 'absolute',
                    right: '0px',
                    marginRight: '20px',
                  }}
                >
                  <Pagination
                    page={pageNumberForTopMedications}
                    count={totalPagesForTopMedications}
                    onChange={handlePageChangeForTopMedications}
                    siblingCount={1}
                    boundaryCount={1}
                    shape={'rounded'}
                    size="small"
                  />
                </Grid>
              </>
            )
          }
        />
      </Grid>
      <Grid style={{ display: 'flex', marginBottom: '28px' }}>
        <div style={{ marginRight: '24px' }}>
          <CardComponent
            title={cardMessages.TOP_PROCEDURES.TITLE}
            subTitle={
              cardMessages.TOP_PROCEDURES.SUB_TITLE +
              getDropdownValue(proceduresDropdownValue)
            }
            width="576px"
            height="405px"
            dropdownElement={
              <DashedStandardDropdown
                options={cardMessages.TOP_PROCEDURES.DROPDOWN_OPTIONS}
                displayValue={cardMessages.TOP_PROCEDURES.DROPDOWN_OPTIONS}
                value={proceduresDropdownValue}
                handleChange={(event: any) => {
                  const value: string = event.target.value as string;
                  setProceduresDropdownValue(value);
                  setPageNumberForTopProcedures(1);
                }}
              />
            }
            body={
              proceduresLoader ? (
                <Box
                  style={{
                    display: 'flex',
                    height: '259px',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <CircularLoader />
                </Box>
              ) : (
                <>
                  <Grid style={{ margin: '7px 0px 0px 6px' }}>
                    <BasicTable
                      columns={columnsForProcedures}
                      cellHeight="44px"
                      cellPadding="12px 18px 12px 28px"
                      rowsData={getDropdownDetails(proceduresDropdownValue)}
                      displayType="row"
                      tableHeight="259px"
                    />
                  </Grid>
                  <Grid
                    style={{
                      marginTop: '16px',
                      position: 'absolute',
                      right: '0px',
                      marginRight: '20px',
                    }}
                  >
                    <Pagination
                      page={pageNumberForTopProcedures}
                      count={totalPagesForTopProcedures}
                      onChange={handlePageChangeForTopProcedures}
                      siblingCount={1}
                      boundaryCount={1}
                      shape={'rounded'}
                      size="small"
                    />
                  </Grid>
                </>
              )
            }
          />
        </div>
        <CardComponent
          title={cardMessages.TOP_PROVIDERS.TITLE}
          subTitle={
            cardMessages.TOP_PROVIDERS.SUB_TITLE +
            getDropdownValue(providersDropdownValue)
          }
          width="576px"
          height="405px"
          dropdownElement={
            <DashedStandardDropdown
              options={cardMessages.TOP_PROVIDERS.DROPDOWN_OPTIONS}
              displayValue={cardMessages.TOP_PROVIDERS.DROPDOWN_OPTIONS}
              value={providersDropdownValue}
              handleChange={(event: any) => {
                const value: string = event.target.value as string;
                setProvidersDropdownValue(value);
                setPageNumberForTopProviders(1);
              }}
            />
          }
          body={
            providersLoader ? (
              <Box
                style={{
                  display: 'flex',
                  height: '259px',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <CircularLoader />
              </Box>
            ) : (
              <>
                <Grid style={{ margin: '7px 0px 0px 6px' }}>
                  <BasicTable
                    columns={columnsForProviders}
                    cellHeight="44px"
                    cellPadding="12px 18px 12px 28px"
                    rowsData={getDropdownDetails(providersDropdownValue)}
                    displayType="row"
                    tableHeight="259px"
                  />
                </Grid>
                <Grid
                  style={{
                    marginTop: '16px',
                    position: 'absolute',
                    right: '0px',
                    marginRight: '20px',
                  }}
                >
                  <Pagination
                    page={pageNumberForTopProviders}
                    count={totalPagesForTopProviders}
                    onChange={handlePageChangeForTopProviders}
                    siblingCount={1}
                    boundaryCount={1}
                    shape={'rounded'}
                    size="small"
                  />
                </Grid>
              </>
            )
          }
        />
      </Grid>
    </>
  );
};
export default TopConditionsTable;
